import * as DropdownMenu from "@radix-ui/react-dropdown-menu"
import clsx from "clsx"
import { useRef, useState } from "react"

import useIsDesktop from "@hooks/useIsDesktop"
import useLayerStack from "@hooks/useLayerStack"

import generateRandomKey from "@utils/generateRandomKey"

import { Divider } from "@atoms"

import { MenuItem, Popover } from "@molecules"
import { DefaultMenuItemProps } from "@molecules/MenuItem/MenuItem.types"
import PopoverHeader from "@molecules/Popover/PopoverHeader/PopoverHeader"

import { ComponentNavigation } from "@organisms"

import styles from "./Menu.module.scss"
import { MenuContentNode, MenuItemType, MenuProps } from "./Menu.types"

export default function Menu(props: MenuProps) {
    const {
        title,
        headerLeft,
        trigger,
        items,
        className,
        isOpen,
        setIsOpen,
        popoverSide = "right",
        popoverAlign = "end",
    } = props

    const isDesktop = useIsDesktop()
    const closedByPointer = useRef(false)
    const [internalIsOpen, setInternalIsOpen] = useState(false)
    const isMenuOpen = isOpen ?? internalIsOpen
    const handleOpenChange = (open: boolean) => {
        setIsOpen?.(open)
        if (typeof isOpen !== "boolean") {
            setInternalIsOpen(open)
        }
    }
    const { floatingZIndex } = useLayerStack(isDesktop ? isMenuOpen : false)

    const renderDesktopNodes = (nodes: MenuContentNode[]) => {
        return nodes.map((node) => {
            if (node.type === "divider") {
                return (
                    <div key={generateRandomKey()} className={styles.divider}>
                        <Divider />
                    </div>
                )
            } else {
                return (
                    <div key={generateRandomKey()} className={styles.menuGroup}>
                        {node.items.map((menuItems: MenuItemType) => {
                            if ("type" in menuItems && menuItems.type === "labelItem") {
                                if (menuItems.hideOnMobile && !isDesktop) {
                                    return null
                                }
                                return (
                                    <div key={generateRandomKey()} className={styles.menuItemLabel}>
                                        {menuItems.text}
                                    </div>
                                )
                            }
                            const { submenu, icon, label, className, ...rest } = menuItems as Exclude<
                                MenuItemType,
                                { type: "labelItem" }
                            >
                            if (submenu) {
                                return (
                                    <DropdownMenu.Sub key={generateRandomKey()}>
                                        <DropdownMenu.SubTrigger asChild={true}>
                                            <MenuItem
                                                {...(rest as DefaultMenuItemProps)}
                                                icon={icon}
                                                label={label}
                                                showSubMenu={true}
                                            />
                                        </DropdownMenu.SubTrigger>
                                        <DropdownMenu.Portal>
                                            <DropdownMenu.SubContent
                                                className={clsx(styles.menuContent, submenu.className)}
                                                sideOffset={4}
                                                collisionPadding={4}
                                                loop={true}
                                                avoidCollisions={true}
                                                style={{ zIndex: floatingZIndex }}
                                            >
                                                {renderDesktopNodes(submenu.items)}
                                            </DropdownMenu.SubContent>
                                        </DropdownMenu.Portal>
                                    </DropdownMenu.Sub>
                                )
                            } else if (rest.variant === "toggle") {
                                return (
                                    <DropdownMenu.Item
                                        asChild={true}
                                        key={generateRandomKey()}
                                        onSelect={(e) => e.preventDefault()}
                                    >
                                        <MenuItem icon={icon} label={label} className={className} {...rest} />
                                    </DropdownMenu.Item>
                                )
                            } else {
                                return (
                                    <DropdownMenu.Item asChild={true} key={generateRandomKey()}>
                                        <MenuItem icon={icon} label={label} className={className} {...rest} />
                                    </DropdownMenu.Item>
                                )
                            }
                        })}
                    </div>
                )
            }
        })
    }

    const renderMobilePrimary = (nodes: MenuContentNode[]) => {
        const renderNodes = (levelNodes: MenuContentNode[], parentId: string) => {
            return levelNodes.map((node, groupIndex) => {
                if (node.type === "divider") {
                    return (
                        <div key={generateRandomKey()} className={styles.divider}>
                            <Divider />
                        </div>
                    )
                } else {
                    return (
                        <div key={generateRandomKey()} className={styles.menuGroup}>
                            {node.items.map((menuItems: MenuItemType, itemIndex: number) => {
                                if ("type" in menuItems && menuItems.type === "labelItem") {
                                    if (menuItems.hideOnMobile) {
                                        return null
                                    }
                                    return (
                                        <div key={generateRandomKey()} className={styles.menuItemLabel}>
                                            {menuItems.text}
                                        </div>
                                    )
                                }
                                const { submenu, ...rest } = menuItems as Exclude<MenuItemType, { type: "labelItem" }>
                                const childId = `${parentId}-${groupIndex}-${itemIndex}`
                                if (submenu) {
                                    return (
                                        <ComponentNavigation.Navigate
                                            key={generateRandomKey()}
                                            to={childId}
                                            asChild={true}
                                        >
                                            <MenuItem {...(rest as DefaultMenuItemProps)} showSubMenu={true} />
                                        </ComponentNavigation.Navigate>
                                    )
                                } else {
                                    return <MenuItem key={generateRandomKey()} {...rest} />
                                }
                            })}
                        </div>
                    )
                }
            })
        }

        return (
            <Popover.View componentId="primary">
                <Popover.Section animationType="fade">
                    <Popover.Header.Root>
                        {headerLeft && <Popover.Header.LeftSlot>{headerLeft}</Popover.Header.LeftSlot>}
                        <Popover.Header.Title title={title || ""} />
                    </Popover.Header.Root>
                </Popover.Section>
                <Popover.Section animationType="slide">
                    <Popover.Body>{renderNodes(nodes, "primary")}</Popover.Body>
                </Popover.Section>
            </Popover.View>
        )
    }

    const buildMobileSubViews = (nodes: MenuContentNode[], parentId: string): React.ReactNode[] => {
        const views: React.ReactNode[] = []

        const renderNodes = (levelNodes: MenuContentNode[], currentId: string) => {
            return levelNodes.map((node, groupIndex) => {
                if (node.type === "divider") {
                    return (
                        <div key={generateRandomKey()} className={styles.divider}>
                            <Divider />
                        </div>
                    )
                } else {
                    return (
                        <div key={generateRandomKey()} className={styles.menuGroup}>
                            {node.items.map((menuItems: MenuItemType, itemIndex: number) => {
                                if ("type" in menuItems && menuItems.type === "labelItem") {
                                    if (menuItems.hideOnMobile) {
                                        return null
                                    }
                                    return (
                                        <div key={generateRandomKey()} className={styles.menuItemLabel}>
                                            {menuItems.text}
                                        </div>
                                    )
                                }
                                const { submenu, ...rest } = menuItems as Exclude<MenuItemType, { type: "labelItem" }>
                                const childId = `${currentId}-${groupIndex}-${itemIndex}`
                                if (submenu) {
                                    return (
                                        <ComponentNavigation.Navigate
                                            key={generateRandomKey()}
                                            to={childId}
                                            asChild={true}
                                        >
                                            <MenuItem {...(rest as DefaultMenuItemProps)} showSubMenu={true} />
                                        </ComponentNavigation.Navigate>
                                    )
                                } else {
                                    return <MenuItem key={generateRandomKey()} {...rest} />
                                }
                            })}
                        </div>
                    )
                }
            })
        }

        nodes.forEach((node, groupIndex) => {
            if (node.type === "group") {
                node.items.forEach((menuItems, itemIndex) => {
                    if ("type" in menuItems && menuItems.type === "labelItem") {
                        return
                    }
                    const { submenu } = menuItems as Exclude<MenuItemType, { type: "labelItem" }>
                    if (submenu) {
                        const currentId = `${parentId}-${groupIndex}-${itemIndex}`
                        const header = submenu.header
                        views.push(
                            <Popover.View
                                componentId={currentId}
                                key={generateRandomKey()}
                                className={submenu.className}
                            >
                                {header?.title && (
                                    <Popover.Section animationType="fade">
                                        <PopoverHeader.Root>
                                            <PopoverHeader.BackButton backDestination={parentId} aria-label="Back" />
                                            <PopoverHeader.Title title={header.title} />
                                            {header.showCloseButton && <PopoverHeader.CloseButton />}
                                        </PopoverHeader.Root>
                                    </Popover.Section>
                                )}
                                <Popover.Section animationType="slide">
                                    <Popover.Body>{renderNodes(submenu.items, currentId)}</Popover.Body>
                                </Popover.Section>
                            </Popover.View>,
                        )

                        // Recurse into deeper submenus
                        views.push(...buildMobileSubViews(submenu.items, currentId))
                    }
                })
            }
        })

        return views
    }

    if (isDesktop) {
        return (
            <DropdownMenu.Root open={isMenuOpen} onOpenChange={handleOpenChange}>
                <DropdownMenu.Trigger asChild={true}>{trigger}</DropdownMenu.Trigger>
                <DropdownMenu.Portal>
                    <DropdownMenu.Content
                        className={clsx(styles.menuContent, className)}
                        sideOffset={4}
                        collisionPadding={4}
                        avoidCollisions={true}
                        loop={true}
                        side={popoverSide}
                        align={popoverAlign}
                        onPointerDownOutside={() => {
                            closedByPointer.current = true
                        }}
                        onCloseAutoFocus={(event) => {
                            if (closedByPointer.current) {
                                event.preventDefault()
                                closedByPointer.current = false
                            }
                        }}
                        style={{ zIndex: floatingZIndex }}
                    >
                        {renderDesktopNodes(items)}
                    </DropdownMenu.Content>
                </DropdownMenu.Portal>
            </DropdownMenu.Root>
        )
    } else {
        return (
            <Popover.Root isOpen={isMenuOpen} onOpenChange={handleOpenChange}>
                <Popover.Trigger>{trigger}</Popover.Trigger>
                <Popover.Viewport side="right" align="end">
                    {renderMobilePrimary(items)}
                    {buildMobileSubViews(items, "primary")}
                </Popover.Viewport>
            </Popover.Root>
        )
    }
}
